<script>
// utils and composables
import { login } from 'v3/api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import Slogan from '../SloganBox.vue';

// components
import FormInput from '../../components/Form/Input.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import GoogleOAuthButton from '../../../../../v3/components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import GapifyHeader from '../AuthHeader.vue';
import AuthLayout from '../AuthLayout.vue';
import { BaseButton } from '../../components/Button';
import OnboardingMotionLogin from './OnboardingMotionLogin.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
};

export default {
  components: {
    BaseButton,
    GapifyHeader,
    FormInput,
    GoogleOAuthButton,
    MfaVerification,
    Spinner,
    AuthLayout,
    OnboardingMotionLogin,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    showGoogleOAuth() {
      return Boolean(window.chatwootConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.chatwootConfig.signupEnabled);
    },
    isSubmitDisabled() {
      if (this.email) return true;

      const { email, password } = this.credentials;
      const empty = !email?.trim() || !password;

      return this.loginApi.showLoading || empty || this.v$.credentials.$invalid;
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const message = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      useAlert(this.$t(message));
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleEnterKey(event) {
      if (event.key === 'Enter' && !this.isSubmitDisabled) {
        event.preventDefault();
        this.submitFormLogin();
      }
    },
    handleMfaVerified() {
      window.location = '/app';
    },
    handleMfaCancel() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
    handleMfaExpired() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
      this.showAlertMessage(this.$t('MFA_VERIFICATION.TOKEN_EXPIRED'));
    },
  },
};
</script>

<template>
  <AuthLayout>
    <template #left>
      <div
        class="flex flex-col w-full h-full min-[1440px]:justify-start justify-between px-4 min-[1440px]:px-10 min-[960px]:px-8 min-[1440px]:py-10 min-[960px]:pt-8 dark:bg-slate-900"
      >
        <MfaVerification
          v-if="mfaRequired"
          :mfa-token="mfaToken"
          @verified="handleMfaVerified"
          @cancel="handleMfaCancel"
          @expired="handleMfaExpired"
        />
        <div v-else class="flex flex-col">
          <section class="flex flex-col w-full">
            <GapifyHeader
              :title-text="`${$t('LOGIN.TITLE')}`"
              :show-description="showSignupLink"
              :description="$t('LOGIN.NO_ACCOUNT_YET')"
              description-class="!text-2 !mt-1 text-descriptionText dark:!text-slate-400"
            >
              <template #description>
                <div
                  class="flex flex-col min-[960px]:flex-row text-descriptionText dark:text-slate-300 text-[14px] min-[1440px]:text-[16px] gap-0 items-center"
                >
                  {{ $t('LOGIN.NO_ACCOUNT_YET') }}
                  <BaseButton
                    type="Text"
                    size="Small"
                    :text="$t('LOGIN.CREATE_NEW_ACCOUNT')"
                    class="!px-3"
                    @click="$router.push({ path: 'auth/signup' })"
                  />
                </div>
              </template>
            </GapifyHeader>
            <section
              class="flex flex-col w-full"
              :class="{
                'mb-8 mt-6': !showGoogleOAuth,
                'animate-wiggle': loginApi.hasErrored,
              }"
            >
              <div v-if="!email">
                <GoogleOAuthButton v-if="showGoogleOAuth" />
                <form @submit.prevent="submitFormLogin" class="flex flex-col gap-6">
                  <FormInput
                    v-model="credentials.email"
                    name="email_address"
                    type="text"
                    data-testid="email_input"
                    :tabindex="1"
                    :label="$t('LOGIN.EMAIL.LABEL')"
                    :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                    :has-error="v$.credentials.email.$error"
                    :error-message="$t('LOGIN.EMAIL.ERROR')"
                    :is-first-item="true"
                    required
                    is-first-item
                    @input="v$.credentials.email.$touch"
                    @keydown.enter="handleEnterKey"
                  />
                  <FormInput
                    v-model="credentials.password"
                    type="password"
                    name="password"
                    data-testid="password_input"
                    :tabindex="2"
                    :label="$t('LOGIN.PASSWORD.LABEL')"
                    :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                    :has-error="v$.credentials.password.$error"
                    required
                    is-first-item
                    @input="v$.credentials.password.$touch"
                    @keydown.enter="handleEnterKey"
                  />
                </form>
              </div>

              <div v-else class="flex items-center justify-center py-10">
                <Spinner color-scheme="primary" />
              </div>
            </section>
          </section>
        </div>
        <div
          v-if="!mfaRequired"
          class="flex flex-col min-[1440px]:flex-row min-[1440px]:justify-between gap-4 min-[1440px]:gap-0 min-[1440px]:items-center min-[1440px]:flex-shrink-0 py-6 min-[1440px]:pt-12"
        >
          <BaseButton
            v-if="!globalConfig.disableUserProfileUpdate"
            status="Default"
            type="Text"
            :text="$t('LOGIN.FORGOT_PASSWORD')"
            class="!px-3 order-2 min-[1440px]:order-1"
            @click="$router.push({ path: 'auth/reset/password' })"
          />
          <BaseButton
            status="gapify"
            type="Fill"
            size="Default"
            :text="$t('LOGIN.SUBMIT')"
            :loading="loginApi.showLoading"
            :disabled="isSubmitDisabled"
            tabindex="3"
            button-type="submit"
            class="w-full min-[1440px]:w-auto order-1 min-[1440px]:order-2"
            @click="submitFormLogin"
          />
        </div>
      </div>
    </template>
    <template #right>
      <OnboardingMotionLogin />
    </template>
  </AuthLayout>
</template>
